/**
 * FeedCard Component
 * 
 * Individual feed card component displaying inspection report with images and text
 * Responsive: Card layout for desktop, full-width Instagram-style for mobile
 * Features: Image carousel, swipe gestures, click-to-zoom
 */

import React, { useState, useRef } from 'react';
import {
    Box,
    Card,
    CardBody,
    CardHeader,
    HStack,
    VStack,
    Avatar,
    Text,
    Image,
    IconButton,
    Badge,
    Flex,
    useColorModeValue,
    useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { FeedItem } from './types';
import { getFullImageUrl } from './utils';

interface FeedCardProps {
    item: FeedItem;
}

export const FeedCard: React.FC<FeedCardProps> = ({ item }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const touchStartX = useRef<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const bgColor = useColorModeValue('white', 'gray.800');
    const textColor = useColorModeValue('gray.700', 'gray.200');
    const metaColor = useColorModeValue('gray.500', 'gray.400');

    // Determine if mobile view
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Convert image URLs to full paths using utility function
    const fullImageUrls = item.image_urls.map(getFullImageUrl);
    const hasMultipleImages = fullImageUrls.length > 1;

    const handlePrevImage = () => {
        if (!hasMultipleImages) return;
        setCurrentImageIndex((prev) =>
            prev === 0 ? fullImageUrls.length - 1 : prev - 1
        );
    };

    const handleNextImage = () => {
        if (!hasMultipleImages) return;
        setCurrentImageIndex((prev) =>
            prev === fullImageUrls.length - 1 ? 0 : prev + 1
        );
    };

    function linkify(text: string) {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text.split(urlRegex).map((part, i) => {
            if (part.match(urlRegex)) {
                return (
                    <a
                        key={i}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#4A5A33", textDecoration: "underline" }}
                    >
                        {part}
                    </a>
                );
            }
            return part;
        });
    }

    // Swipe gesture handlers - only active for multiple images
    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!hasMultipleImages) return;
        touchStartX.current = e.touches[0].clientX;
        setIsDragging(true);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!hasMultipleImages || !isDragging || touchStartX.current === null || !containerRef.current) return;
        const currentX = e.touches[0].clientX;
        const diff = currentX - touchStartX.current;
        setDragOffset(diff);
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!hasMultipleImages || !isDragging || touchStartX.current === null || !containerRef.current) {
            setDragOffset(0);
            setIsDragging(false);
            return;
        }

        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchEndX - touchStartX.current;
        const containerWidth = containerRef.current.offsetWidth;
        const threshold = containerWidth * 0.2; // 20% of container width

        if (Math.abs(diff) > threshold) {
            if (diff > 0 && currentImageIndex > 0) {
                setCurrentImageIndex(currentImageIndex - 1);
            } else if (diff < 0 && currentImageIndex < fullImageUrls.length - 1) {
                setCurrentImageIndex(currentImageIndex + 1);
            }
        }

        setDragOffset(0);
        setIsDragging(false);
        touchStartX.current = null;
    };

    const formattedDate = format(
        new Date(item.inspection_date),
        'dd MMMM yyyy, HH:mm',
        { locale: idLocale }
    );

    // Calculate transform for carousel - only for multiple images
    const getTransform = () => {
        if (!hasMultipleImages || !containerRef.current) return 'translateX(0)';
        const containerWidth = containerRef.current.offsetWidth;
        const baseTranslate = -currentImageIndex * containerWidth;
        return `translateX(${baseTranslate + dragOffset}px)`;
    };

    // Mobile layout
    if (isMobile) {
        return (
            <Box
                bg={bgColor}
                width="100%"
                borderBottom="1px solid"
                borderColor={borderColor}
            >
                {/* Header - Author Info */}
                <Box px={4} py={3}>
                    <HStack spacing={3}>
                        <Avatar
                            name={item.author.name}
                            size="sm"
                            bg="customGreen.500"
                            color="white"
                        />
                        <VStack align="start" spacing={0} flex={1}>
                            <Text fontWeight="bold" fontSize="sm">
                                {item.author.name}
                            </Text>
                            <HStack spacing={2}>
                                <Badge colorScheme="green" fontSize="2xs">
                                    {item.author.job_position}
                                </Badge>
                            </HStack>
                        </VStack>
                    </HStack>
                </Box>

                {/* Image Carousel - Full Width */}
                {fullImageUrls.length > 0 && (
                    <Box
                        ref={containerRef}
                        position="relative"
                        width="100%"
                        bg="black"
                        overflow="hidden"
                        onTouchStart={hasMultipleImages ? handleTouchStart : undefined}
                        onTouchMove={hasMultipleImages ? handleTouchMove : undefined}
                        onTouchEnd={hasMultipleImages ? handleTouchEnd : undefined}
                        style={{ touchAction: hasMultipleImages ? 'pan-y' : 'auto' }}
                    >
                        <Box
                            display="flex"
                            height="400px"
                            style={{
                                transition: hasMultipleImages && !isDragging ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
                                transform: getTransform(),
                            }}
                        >
                            {fullImageUrls.map((url, index) => (
                                <Box
                                    key={index}
                                    flexShrink={0}
                                    width="100%"
                                    height="100%"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Zoom>
                                        <Image
                                            src={url}
                                            alt={`Inspection ${item.id} - Image ${index + 1}`}
                                            width="100%"
                                            height="100%"
                                            objectFit="cover"
                                            fallbackSrc="https://via.placeholder.com/600x400?text=Image+Not+Available"
                                            draggable={false}
                                            userSelect="none"
                                        />
                                    </Zoom>
                                </Box>
                            ))}
                        </Box>

                        {/* Image Navigation - Hidden on mobile, use swipe instead */}
                        {hasMultipleImages && (
                            <>
                                {/* Image Indicator Dots */}
                                <Flex
                                    position="absolute"
                                    bottom={3}
                                    left="50%"
                                    transform="translateX(-50%)"
                                    gap={2}
                                >
                                    {fullImageUrls.map((_, index) => (
                                        <Box
                                            key={index}
                                            width="6px"
                                            height="6px"
                                            borderRadius="full"
                                            bg={index === currentImageIndex ? 'white' : 'whiteAlpha.500'}
                                            transition="all 0.2s"
                                        />
                                    ))}
                                </Flex>
                            </>
                        )}
                    </Box>
                )}

                {/* Body - Text Content */}
                <Box px={4} py={3}>
                    <Text fontSize="xs" color={metaColor} mb={1}>
                        {formattedDate}
                    </Text>
                    <Text
                        fontSize="sm"
                        color={textColor}
                        whiteSpace="pre-wrap"
                        lineHeight="tall"
                    >
                        {linkify(item.generated_text)}
                    </Text>
                    <Text fontSize="xs" color={metaColor}>
                        #{item.short_uuid}
                    </Text>
                </Box>
            </Box>
        );
    }

    // Desktop Card Layout (Original)
    return (
        <Card
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            overflow="hidden"
            bg={bgColor}
            shadow="md"
            _hover={{ shadow: 'lg' }}
            transition="all 0.2s"
        >
            {/* Header - Author Info */}
            <CardHeader pb={2}>
                <HStack spacing={3}>
                    <Avatar
                        name={item.author.name}
                        size="md"
                        bg="customGreen.500"
                        color="white"
                    />
                    <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="bold" fontSize="md">
                            {item.author.name}
                        </Text>
                        <HStack spacing={2}>
                            <Badge colorScheme="green" fontSize="xs">
                                {item.author.job_position}
                            </Badge>
                            <Text fontSize="xs" color={metaColor}>
                                {formattedDate}
                            </Text>
                        </HStack>
                    </VStack>
                </HStack>
            </CardHeader>

            {/* Image Carousel */}
            {fullImageUrls.length > 0 && (
                <Box
                    ref={containerRef}
                    position="relative"
                    width="100%"
                    bg="gray.100"
                    overflow="hidden"
                    onTouchStart={hasMultipleImages ? handleTouchStart : undefined}
                    onTouchMove={hasMultipleImages ? handleTouchMove : undefined}
                    onTouchEnd={hasMultipleImages ? handleTouchEnd : undefined}
                    style={{ touchAction: hasMultipleImages ? 'pan-y' : 'auto' }}
                >
                    <Box
                        display="flex"
                        height={{ base: '300px', md: '400px', lg: '500px' }}
                        style={{
                            transition: hasMultipleImages && !isDragging ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
                            transform: getTransform(),
                        }}
                    >
                        {fullImageUrls.map((url, index) => (
                            <Box
                                key={index}
                                flexShrink={0}
                                width="100%"
                                height="100%"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Zoom>
                                    <Image
                                        src={url}
                                        alt={`Inspection ${item.id} - Image ${index + 1}`}
                                        width="100%"
                                        height="100%"
                                        objectFit="cover"
                                        fallbackSrc="https://via.placeholder.com/600x400?text=Image+Not+Available"
                                        draggable={false}
                                        userSelect="none"
                                    />
                                </Zoom>
                            </Box>
                        ))}
                    </Box>

                    {/* Image Navigation */}
                    {hasMultipleImages && (
                        <>
                            {currentImageIndex > 0 && (
                                <IconButton
                                    aria-label="Previous image"
                                    icon={<ChevronLeftIcon boxSize={6} />}
                                    position="absolute"
                                    left={2}
                                    top="50%"
                                    transform="translateY(-50%)"
                                    onClick={handlePrevImage}
                                    colorScheme="blackAlpha"
                                    variant="solid"
                                    size="sm"
                                    isRound
                                />
                            )}
                            {currentImageIndex < fullImageUrls.length - 1 && (
                                <IconButton
                                    aria-label="Next image"
                                    icon={<ChevronRightIcon boxSize={6} />}
                                    position="absolute"
                                    right={2}
                                    top="50%"
                                    transform="translateY(-50%)"
                                    onClick={handleNextImage}
                                    colorScheme="blackAlpha"
                                    variant="solid"
                                    size="sm"
                                    isRound
                                />
                            )}

                            {/* Image Indicator Dots */}
                            <Flex
                                position="absolute"
                                bottom={3}
                                left="50%"
                                transform="translateX(-50%)"
                                gap={2}
                            >
                                {fullImageUrls.map((_, index) => (
                                    <Box
                                        key={index}
                                        width="8px"
                                        height="8px"
                                        borderRadius="full"
                                        bg={index === currentImageIndex ? 'white' : 'whiteAlpha.500'}
                                        cursor="pointer"
                                        onClick={() => setCurrentImageIndex(index)}
                                        transition="all 0.2s"
                                    />
                                ))}
                            </Flex>
                        </>
                    )}
                </Box>
            )}

            {/* Body - Text Content */}
            <CardBody pt={3}>
                <Text
                    fontSize="sm"
                    color={textColor}
                    whiteSpace="pre-wrap"
                    lineHeight="tall"
                >
                    {linkify(item.generated_text)}
                </Text>
                <Text fontSize="xs" color={metaColor}>
                    #{item.short_uuid}
                </Text>
            </CardBody>
        </Card>
    );
};